import type {
  GroupRecord,
  StreamAnalytics,
  StreamRecord,
} from '@vlabs/api/artemy/model';
import { get, has, isEqual } from 'lodash';

import { normalizeStreamAnalyticsRecordItem } from '../records';
import { collectObjectPaths, findGroupAnalyticsItem } from './checkStreamSync';

const ROI_SKIP_KEYS = ['roi', 'droi'];

function getParametersObject(item: unknown): Record<string, unknown> | undefined {
  const parameters = (item as { parameters?: { parameters?: unknown } })?.parameters?.parameters;

  return (typeof parameters === 'object' && parameters !== null && !Array.isArray(parameters))
    ? parameters as Record<string, unknown>
    : undefined;
}

/**
 * Пофилдовые статусы синхронизации параметров ВА потока:
 * путь параметра (относительно parameters.parameters) → true — личное значение
 * совпадает с групповым (зелёная), false — отличается (серая). Пути, которых
 * нет в групповой ВА, в результат не попадают — иконка не показывается.
 * ROI/DROI исключены (личные зоны потока).
 *
 * Личная ВА (enriched=0) матчится к групповой цепочкой label/type →
 * handler_id → type (аналитика с рассинхронизированным label). Без личной
 * записи все поля считаются унаследованными (true).
 */
export function getStreamAnalyticsParamsSyncStatus(
  group: GroupRecord | undefined,
  stream: StreamRecord | undefined,
  analyticsItem: StreamAnalytics,
): Record<string, boolean> {
  const status: Record<string, boolean> = {};
  if (!group || !stream) return status;

  const groupItem = findGroupAnalyticsItem(group.analytics, analyticsItem);
  if (!groupItem) return status;

  const personalItem = findGroupAnalyticsItem(
    (stream.analytics ?? [])
      .map((item) => normalizeStreamAnalyticsRecordItem(item)) as unknown as StreamAnalytics[],
    analyticsItem,
  );

  const groupParams = getParametersObject(groupItem);
  if (!groupParams) return status;

  const personalParams = getParametersObject(personalItem);

  collectObjectPaths(groupParams, ROI_SKIP_KEYS).forEach((path) => {
    if (!personalParams || !has(personalParams, path)) {
      // Личного значения нет — поле наследуется от группы
      status[path] = true;
      return;
    }

    status[path] = isEqual(get(personalParams, path), get(groupParams, path));
  });

  return status;
}
